import * as React from 'react';
import { Canceler } from 'axios';

import { IItem, IItemsProps, IItemsState } from './types';
import { ITagGroup } from '../TagGroups/types';
import { IParameter } from '../Parameters/types';

import API from 'src/services/api';
import { MarketContext } from 'src/services/contexts/markets/MarketContext';
import New from './components/New';
import Edit from './components/Edit';
import Subpage from '../components/Subpage';
import {
  mapDataToOptions,
  mapItemsWithParameter,
} from './services/mapDataToOptions';
import { columns, getColumnValues } from './services/columnData';
import { ICache, createCache } from './services/cache';

class Items extends React.Component<IItemsProps, IItemsState> {
  static contextType = MarketContext;
  context!: React.ContextType<typeof MarketContext>;
  itemOptionsWithParameter: any[];
  reqCancelers: Canceler[] = [];
  cache: ICache;

  state: IItemsState = {
    items: [],
    archivedItems: [],
    parameterOptions: [],
    tagGroupOptions: [],
    marketOptions: [],
    tagGroups: [],
    isLoading: true,
    isArchive: false,
  };

  async componentDidMount() {
    const itemReq = API.ADMIN.ITEMS.getAll();
    const requests = [
      itemReq,
      API.ADMIN.PARAMETERS.get(),
      API.ADMIN.TAG_GROUPS.getAll(),
    ];
    this.reqCancelers = requests.map((req) => req.cancel);
    const [allItems, parameters, tagGroups] = await Promise.all(
      requests.map((req) => req.getData()) as [
        Promise<IItem[]>,
        Promise<IParameter[]>,
        Promise<ITagGroup[]>
      ]
    );
    this.reqCancelers = [];

    this.cache = createCache(itemReq.updateCache, allItems);

    const markets = this.context.localContentAdminProfiles;
    const mappedData = mapDataToOptions({
      parameters,
      allItems,
      tagGroups,
      markets,
    });
    this.setState(
      {
        ...mappedData,
        tagGroups,
        isLoading: false,
      },
      this.setItemsWithParameter
    );
  }

  componentWillUnmount() {
    this.reqCancelers.forEach((cancel) => cancel());
  }

  getSearchString = (item: IItem) =>
    `${item.parameter.name} (${item.parameter.id}) ${item.name} (${item.id})`;

  add = async (item: IItem) => {
    const newItem = await API.ADMIN.ITEMS.add(item);

    this.cache.add(newItem);

    this.setState(
      ({ items }) => ({
        items: [newItem, ...items],
      }),
      this.setItemsWithParameter
    );
  };

  update = async (item: IItem) => {
    const updatedItem = await API.ADMIN.ITEMS.update(item);

    this.cache.update(updatedItem);

    this.setState(({ items }) => {
      const itemIndex = items.findIndex((el) => el.id === updatedItem.id);
      return {
        items: [
          ...items.slice(0, itemIndex),
          updatedItem,
          ...items.slice(itemIndex + 1),
        ],
      };
    }, this.setItemsWithParameter);
  };

  remove = (item: IItem) => async () => {
    if (window.confirm('Deleting is irreversible, continue?')) {
      await API.ADMIN.ITEMS.remove(item.id);

      this.cache.delete(item);

      this.setState(
        ({ items }) => ({
          items: items.filter((el) => el.id !== item.id),
        }),
        this.setItemsWithParameter
      );
    }
  };

  toggleArchive = (item: IItem) => () => {
    const isArchived = item.isDeleted;
    const updatedItem = {
      ...item,
      isDeleted: !isArchived,
    };
    API.ADMIN.ITEMS.update(updatedItem);

    this.cache.update(updatedItem);

    this.setState(
      (state) => ({
        items: isArchived
          ? [...state.items, updatedItem]
          : state.items.filter((el) => el.id !== item.id),
        archivedItems: !isArchived
          ? [...state.archivedItems, updatedItem]
          : state.archivedItems.filter((el) => el.id !== item.id),
      }),
      this.setItemsWithParameter
    );
  };

  setItemsWithParameter = () => {
    this.itemOptionsWithParameter = mapItemsWithParameter(this.state.items);
  };

  formDeps = () => ({
    itemOptionsWithParameter: this.itemOptionsWithParameter,
    parameterOptions: this.state.parameterOptions,
    tagGroupOptions: this.state.tagGroupOptions,
    marketOptions: this.state.marketOptions,
    tagGroups: this.state.tagGroups,
  });

  NewForm = (props: any) => (
    <New add={this.add} {...this.formDeps()} {...props} />
  );

  EditForm = (props: any) => (
    <Edit
      update={this.update}
      onRemove={this.remove(props.item)}
      onToggleArchive={this.toggleArchive(props.item)}
      {...this.formDeps()}
      {...props}
    />
  );

  render() {
    return (
      <Subpage
        currentPath={this.props.location.pathname}
        NewForm={this.NewForm}
        EditForm={this.EditForm}
        columns={columns}
        getColumnValues={getColumnValues}
        getSearchString={this.getSearchString}
        items={this.state.items}
        archivedItems={this.state.archivedItems}
        isLoading={this.state.isLoading}
        hasArchive={true}
      />
    );
  }
}

export default Items;



// WEBPACK FOOTER //
// ./src/views/Admin/Items/Items.tsx